import React, { useEffect, useState, useRef } from "react";
import { useDebounce } from "@/utils/helpers";

function CandidateScore({
  currentApplication,
  currentVacancy,
  onScoreChange,
  currentScores,
}) {
  const [scores, setScores] = useState({});
  const [totalScore, setTotalScore] = useState(0);

  useEffect(() => {
    // Priority: Use currentScores from parent state if available, then fallback to application data
    if (currentScores && Array.isArray(currentScores)) {
      const initialScores = {};
      currentScores.forEach((score) => {
        initialScores[score.vacancy_scorecard_id] = score.score;
      });
      setScores(initialScores);

      const initialTotalScore = currentVacancy?.scorecards?.reduce(
        (acc, scorecard) => {
          const score = initialScores[scorecard.id] || 0;
          const weight = parseFloat(scorecard.weightage) / 100;
          return acc + score * weight;
        },
        0,
      );
      setTotalScore(initialTotalScore);
    } else if (currentApplication?.total_score?.score_details) {
      const initialScores = {};
      currentVacancy?.scorecards?.forEach((scorecard) => {
        const existingScore = currentApplication.total_score.score_details.find(
          (score) => score.skill === scorecard.skill_name,
        );
        initialScores[scorecard.id] = existingScore?.score ?? 0;
      });
      setScores(initialScores);

      const initialTotalScore = currentVacancy?.scorecards?.reduce(
        (acc, scorecard) => {
          const score = initialScores[scorecard.id] || 0;
          const weight = parseFloat(scorecard.weightage) / 100;
          return acc + score * weight;
        },
        0,
      );
      setTotalScore(initialTotalScore);
    } else {
      setScores({});
      setTotalScore(0);
    }
  }, [currentApplication?.id, currentVacancy?.scorecards, currentScores]);

  const updateScoreAfterDebounce = (updatedScores) => {
    const newTotalScore = currentVacancy?.scorecards?.reduce(
      (acc, scorecard) => {
        const score = updatedScores[scorecard.id] || 0;
        const weight = parseFloat(scorecard.weightage) / 100;
        return acc + score * weight;
      },
      0,
    );
    setTotalScore(newTotalScore);

    const updatedScoreDetails = currentVacancy?.scorecards?.map(
      (scorecard) => ({
        vacancy_scorecard_id: scorecard.id,
        score: updatedScores[scorecard.id] || 0,
      }),
    );

    if (currentApplication?.id) {
      onScoreChange(currentApplication.id, updatedScoreDetails, newTotalScore);
    }
  };

  const [differenceScores, setDifferenceScores] = useState([]);
  useEffect(() => {
    const differenciatedScores =
      currentApplication?.application_score_cards?.filter(
        (appScoreCard) =>
          !currentVacancy?.scorecards?.some(
            (scorecard) => scorecard.id === appScoreCard.vacancy_scorecard_id,
          ),
      );
    setDifferenceScores(differenciatedScores);
    console.log("differenciatedScores", differenciatedScores);
  }, [currentApplication?.application_score_cards, currentVacancy?.scorecards]);

  const debounceScoreUpdate = useDebounce(updateScoreAfterDebounce);

  const handleScoreChange = (score_card_id, newScore) => {
    const parsedScore = parseFloat(newScore);
    const roundedScore = isNaN(parsedScore)
      ? 0
      : Math.round(parsedScore * 10) / 10;
    const validatedScore = Math.min(Math.max(roundedScore, 0), 10);

    const updatedScores = {
      ...scores,
      [score_card_id]: validatedScore,
    };
    setScores(updatedScores);

    debounceScoreUpdate(updatedScores);
  };

  const handleIncrement = (score_card_id) => {
    const current = parseFloat(scores[score_card_id] ?? 0);
    if (current < 10) {
      handleScoreChange(score_card_id, current + 0.5);
    }
  };

  const handleDecrement = (score_card_id) => {
    const current = parseFloat(scores[score_card_id] ?? 0);
    if (current > 0) {
      handleScoreChange(score_card_id, current - 0.5);
    }
  };

  return (
    <div className="bg-card mb-4 rounded-xl shadow-lg ">
      <style jsx>{`
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
      `}</style>

      <div className="rounded-lg">
        <div className="flex items-center justify-between bg-muted p-4 rounded-t-xl shadow-xl">
          <h3 className="text-primary-dark font-semibold">Candidate Score:</h3>
          <span className="text-primary-dark font-semibold">
            {totalScore.toFixed(1)}/10
          </span>
        </div>

        {/* Application Score Cards (Read-only) */}
        {differenceScores?.length > 0 && (
          <div className="">
            {differenceScores.map(({ vacancy_scorecard, score }) => (
              <div
                className="p-4 flex items-center justify-between gap-4 bg-card text-primary-dark"
                key={vacancy_scorecard.id}
              >
                <div className="flex items-center gap-2">
                  <h3 className="text-foreground font-semibold">
                    {vacancy_scorecard.skill_name || "Unknown Skill"}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="w-14 text-center text-primary font-semibold  px-2 py-1 rounded">
                      {score}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Editable Score Cards */}
        {currentVacancy?.scorecards?.map((score_card) => (
          <div
            className="p-4 flex items-center justify-between gap-4"
            key={score_card.id}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-foreground font-semibold">
                {score_card.skill_name}
              </h3>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleDecrement(score_card.id)}
                  className="px-2 py-1 text-primary-dark font-bold text-lg hover:scale-110 transition-transform"
                  disabled={(scores[score_card.id] ?? 0) <= 0}
                >
                  -
                </button>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={scores[score_card.id] ?? ""}
                  onChange={(e) =>
                    handleScoreChange(score_card.id, e.target.value)
                  }
                  className="no-spinner w-14 text-center focus:outline-none text-primary-dark font-semibold bg-transparent"
                  placeholder="0"
                />
                <button
                  onClick={() => handleIncrement(score_card.id)}
                  className="px-2 py-1 text-primary-dark font-bold text-lg hover:scale-110 transition-transform"
                  disabled={(scores[score_card.id] ?? 0) >= 10}
                >
                  +
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default CandidateScore;
